import React, { useEffect, useState, useRef, useCallback } from 'react';
import './CustomAlert.css';

const CustomAlert = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  title,
  message,
  type = 'info', // 'info', 'warning', 'error', 'success', 'confirm'
  confirmText = 'OK',
  cancelText = 'Cancel',
  thirdButtonText = null,
  showCancel = false,
  shareUrl = null,
  onCreateVisualCard = null
}) => {
  const [copiedFeedback, setCopiedFeedback] = useState(false);
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  // Cleanup timeout on unmount and mark as unmounted
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && isMountedRef.current && onClose) {
      onClose();
    }
  };

  // Handle Escape key to close dialog
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMountedRef.current && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Early return after hooks to comply with Rules of Hooks
  if (!isOpen) return null;

  const handleCopyLink = async () => {
    if (shareUrl) {
      try {
        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(shareUrl);
        } else {
          // Fallback for older browsers or insecure contexts
          const textArea = document.createElement('textarea');
          textArea.value = shareUrl;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            document.execCommand('copy');
          } finally {
            document.body.removeChild(textArea);
          }
        }

        setCopiedFeedback(true);

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Set new timeout with extra safety checks
        timeoutRef.current = setTimeout(() => {
          if (timeoutRef.current && isMountedRef.current) {
            try {
              setCopiedFeedback(false);
            } catch (error) {
              // Ignore errors from unmounted component
              console.debug('Ignoring state update on unmounted CustomAlert');
            }
          }
          timeoutRef.current = null;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy link:', err);
        alert('Failed to copy link. Please copy it manually.');
      }
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      case 'success':
        return '✅';
      case 'confirm':
        return '❓';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div className="custom-alert-overlay" onClick={handleBackdropClick}>
      <div className={`custom-alert ${type}`}>
        <div className="custom-alert-header">
          <div className="custom-alert-icon">{getIcon()}</div>
          <h3 className="custom-alert-title">{title}</h3>
          <button
            className="custom-alert-close"
            onClick={() => isMountedRef.current && onClose && onClose()}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="custom-alert-content">
          <p className="custom-alert-message" style={{ whiteSpace: 'pre-line' }}>{message}</p>
          {shareUrl && (
            <div className="share-url-container">
              <label className="share-url-label">Share Link:</label>
              <div className="share-url-input-container">
                <button
                  className={`copy-url-button ${copiedFeedback ? 'copied' : ''}`}
                  onClick={handleCopyLink}
                  title={copiedFeedback ? 'Copied!' : 'Copy link'}
                >
                  {copiedFeedback ? '✓ Copied' : '📋 Copy'}
                </button>
              </div>
              {copiedFeedback && <p className="copy-feedback">Link copied to clipboard!</p>}
              {onCreateVisualCard && (
                <button
                  className="create-visual-card-button"
                  onClick={() => {
                    if (isMountedRef.current) {
                      onClose && onClose();
                      onCreateVisualCard();
                    }
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                  Create Visual Card
                </button>
              )}
            </div>
          )}
        </div>

        {!shareUrl && (
          <div className="custom-alert-actions">
            {thirdButtonText && (
              <button
                className="custom-alert-button third"
                onClick={() => {
                  if (isMountedRef.current && onClose) {
                    onClose();
                  }
                }}
              >
                {thirdButtonText}
              </button>
            )}
            {showCancel && (
              <button
                className="custom-alert-button cancel"
                onClick={() => {
                  if (isMountedRef.current) {
                    if (onCancel) {
                      onCancel();
                    } else if (onClose) {
                      onClose();
                    }
                  }
                }}
              >
                {cancelText}
              </button>
            )}
            <button
              className="custom-alert-button confirm"
              onClick={() => {
                if (isMountedRef.current) {
                  if (onConfirm) {
                    onConfirm();
                  } else if (onClose) {
                    onClose();
                  }
                }
              }}
            >
              {confirmText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomAlert;
